import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import { HttpGateway } from '@core/services/http.gateway'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { OperationRepository } from '@core/operations/operation.repository'
import type {
  OperationHandle,
  OperationOwner,
  OperationResult,
  ReadonlyOperationState,
} from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import { FreshnessRepository } from '@core/infra/freshness/freshness.repository'
import { activeGameSummarySchema, type GameStatus, type Seat } from '@shared/index'

export type GameRowPm = {
  readonly gameId: string
  readonly status: GameStatus
  readonly hostName: string
  readonly guestName: string | null
  readonly yourSeat: Seat
  readonly activeSeat: Seat | null
}

export type LoadGamesFailure = ActionFailurePm & Readonly<{ retryable: boolean }>

export const LOAD_GAMES_OPERATION = defineOperation<null, LoadGamesFailure>({
  id: operationId('games.load'),
  key: () => operationKey('games'),
  groups: () => [operationGroup('session'), operationGroup('game')],
  pending: { kind: 'join' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 15_000,
    toFailure: () => ({ code: 'temporarily-unavailable', retryable: false }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

/**
 * The Games page's own full list — every game the signed-in account holds a
 * seat in, finished ones included (`?include=finished`). Deliberately
 * separate from `ActiveGamesRepository` (`features/game/`): that one polls
 * only non-finished games, app-wide, to feed the sidebar's "Play" section;
 * this one loads once per page visit (`loadIfNeeded`/manual Reload), for the
 * Games page's own list, and never excludes a finished game.
 */
@injectable()
export class GamesListRepository implements UserScopedState {
  gamesPm: readonly GameRowPm[] | null = null

  private readonly dataGateway: HttpGateway
  private readonly freshnessRepository: FreshnessRepository
  private readonly operationOwner: OperationOwner
  private readonly loadOperation: OperationHandle<null, LoadGamesFailure>

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
    @inject(FreshnessRepository) freshnessRepository: FreshnessRepository,
  ) {
    this.dataGateway = dataGateway
    this.freshnessRepository = freshnessRepository
    this.operationOwner = operationRepository.createOwner({ id: 'games-list.repository', lifetime: 'session' })
    this.loadOperation = this.operationOwner.register(LOAD_GAMES_OPERATION)

    makeObservable(this, {
      gamesPm: observable.ref,
    })
  }

  get isLoaded(): boolean {
    return this.gamesPm !== null
  }

  get loadState(): ReadonlyOperationState<LoadGamesFailure> {
    return this.loadOperation.stateFor(null)
  }

  // Route-entry preload target (router.config.ts): reuse what is already in
  // memory rather than re-fetching behind a skeleton on every visit.
  loadIfNeeded = async (): Promise<void> => {
    if (this.isLoaded && this.freshnessRepository.isFresh('games')) return
    await this.load()
  }

  load = async (): Promise<ActionResponsePm> => {
    const result = await this.loadOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/games?include=finished', { signal, idempotencyKey: null })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = activeGameSummarySchema.array().safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data', retryable: false })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (games) => {
          this.gamesPm = games.map((game) =>
            Object.freeze({
              gameId: game.gameId,
              status: game.status,
              hostName: game.hostName,
              guestName: game.guestName,
              yourSeat: game.yourSeat,
              activeSeat: game.activeSeat,
            }),
          )
          this.freshnessRepository.markLoaded('games')
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  // Registered against Types.IUserScopedState — logout resets it without
  // AuthenticationRepository having to know this class exists.
  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.gamesPm = null
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}
